import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import Integration from "@/model/interface/Integration";
import IntegrationSyncToggle from "@/components/pages/integrations/IntegrationSyncToggle";
import {
  useUpdateIntegrationSettingsMutation,
  useUpdateIntegrationSyncSettingsMutation,
  useSyncIntegrationMutation,
} from "@/store/rest_api";
import toast from "react-hot-toast";

interface Props {
  integration: Integration;
}

export default function PodioSettings({ integration }: Props) {
  const [updateSettings] = useUpdateIntegrationSettingsMutation();
  const [updateSyncSettings] = useUpdateIntegrationSyncSettingsMutation();
  const [syncIntegration, { isLoading: isSyncing }] = useSyncIntegrationMutation();
  const settings = integration.settings || {};

  const toggleSetting = async (key: string, value: boolean) => {
    try {
      await updateSettings({ id: integration.id, settings: { [key]: value } }).unwrap();
    } catch {
      toast.error("Failed to update setting");
    }
  };

  const updateSetting = async (key: string, value: unknown) => {
    try {
      await updateSettings({ id: integration.id, settings: { [key]: value } }).unwrap();
    } catch {
      toast.error("Failed to update setting");
    }
  };

  const updateSyncDirection = async (value: string) => {
    try {
      await updateSyncSettings({
        id: integration.id,
        syncEnabled: integration.syncEnabled !== false,
        syncDirection: value,
      }).unwrap();
    } catch {
      toast.error("Failed to update sync direction");
    }
  };

  const handleSync = async () => {
    try {
      await syncIntegration(integration).unwrap();
      toast.success("Contact sync completed");
    } catch {
      toast.error("Sync failed");
    }
  };

  const setupComplete = !!settings.podioContactsAppId && !!settings.podioCallsAppId;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Connection</CardTitle>
          <CardDescription>Podio connection status and sync</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <IntegrationSyncToggle integration={integration} />

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Status</p>
              <p className="text-sm text-muted-foreground">
                {integration.lastSyncAt
                  ? `Last synced ${new Date(integration.lastSyncAt).toLocaleString()}`
                  : "Never synced"}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant={integration.status === "active" ? "default" : "secondary"}>{integration.status}</Badge>
              <Button
                onClick={handleSync}
                disabled={
                  isSyncing || integration.syncEnabled === false || integration.status !== "active" || !setupComplete
                }
                size="sm"
              >
                {isSyncing ? "Syncing..." : "Sync Now"}
              </Button>
            </div>
          </div>
          {!setupComplete && (
            <p className="text-xs text-muted-foreground">
              Finish configuring the contacts and calls apps in the Setup tab before syncing.
            </p>
          )}

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Sync Direction</p>
              <p className="text-sm text-muted-foreground">Controls whether contacts are pulled, pushed, or both</p>
            </div>
            <Select
              value={integration.syncDirection || "BIDIRECTIONAL"}
              onValueChange={(value) => updateSyncDirection(value)}
            >
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="BIDIRECTIONAL">Bidirectional</SelectItem>
                <SelectItem value="PULL_ONLY">Pull Only</SelectItem>
                <SelectItem value="PUSH_ONLY">Push Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Auto Contact Creation</CardTitle>
          <CardDescription>Automatically create Podio items from unknown inbound numbers</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="autoLeadInbound" className="flex flex-col gap-1">
              <span>Inbound calls</span>
              <span className="font-normal text-muted-foreground text-xs">
                Create a Podio contact when an inbound call comes from an unknown number
              </span>
            </Label>
            <Switch
              id="autoLeadInbound"
              checked={!!settings.autoLeadOnInboundCall}
              onCheckedChange={(value) => toggleSetting("autoLeadOnInboundCall", value)}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="autoLeadOutbound" className="flex flex-col gap-1">
              <span>Outbound calls</span>
              <span className="font-normal text-muted-foreground text-xs">
                Create a Podio contact when an outbound call is made to an unknown number
              </span>
            </Label>
            <Switch
              id="autoLeadOutbound"
              checked={!!settings.autoLeadOnOutboundCall}
              onCheckedChange={(value) => toggleSetting("autoLeadOnOutboundCall", value)}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label htmlFor="autoLeadSms" className="flex flex-col gap-1">
              <span>Inbound SMS</span>
              <span className="font-normal text-muted-foreground text-xs">
                Create a Podio contact when an inbound SMS comes from an unknown number
              </span>
            </Label>
            <Switch
              id="autoLeadSms"
              checked={!!settings.autoLeadOnInboundSms}
              onCheckedChange={(value) => toggleSetting("autoLeadOnInboundSms", value)}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label className="flex flex-col gap-1">
              <span>Phone number format</span>
              <span className="font-normal text-muted-foreground text-xs">
                How phone numbers appear in Podio item bodies
              </span>
            </Label>
            <Select
              value={(settings.phoneNumberFormat as string) || "international"}
              onValueChange={(value) => updateSetting("phoneNumberFormat", value)}
            >
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="international">International (+1 555 123 4567)</SelectItem>
                <SelectItem value="national">National ((555) 123-4567)</SelectItem>
                <SelectItem value="compact">Compact (5551234567)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
